import { useId } from 'react';
import {
  KADIR_MARK_FOLDS,
  KADIR_MARK_INK,
  KADIR_MARK_INK_POINTS,
  KADIR_MARK_VIEWBOX,
} from './kadir-mark-art';

/**
 * The Kader mark: the `K` from the supplied brand logo. Kept as a component rather than
 * an asset so it scales with its container and the ink stays in step with the palette.
 *
 * The path data comes from `kadir-mark-art.ts`, which any other renderer draws from too.
 *
 * The accessible name defaults to the Latin brand — an SVG rendered outside a translated
 * context still has to say something, and "Kader" is the Latin spelling settled on
 * 2026-09-22 (the domain and the app agree). Callers inside a translated tree pass the
 * localized name.
 */
export function KadirMark({
  className = 'h-10 w-10',
  title = 'Kader',
}: {
  className?: string;
  title?: string;
}) {
  // Gradient ids are document-wide; two marks on one page (login card and a header)
  // must not share one.
  const id = useId();

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={KADIR_MARK_VIEWBOX}
      role="img"
      aria-label={title}
      className={`shrink-0 ${className}`}
    >
      <title>{title}</title>
      <defs>
        {KADIR_MARK_FOLDS.map(({ gradient: g }, i) => (
          <linearGradient
            key={g.x1}
            id={`${id}-${i}`}
            x1={g.x1}
            y1={g.y1}
            x2={g.x2}
            y2={g.y2}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset=".28" stopColor={g.from} />
            <stop offset="1" stopColor={g.to} />
          </linearGradient>
        ))}
      </defs>
      {KADIR_MARK_INK_POINTS.map((points) => (
        <polygon key={points} points={points} fill={KADIR_MARK_INK} />
      ))}
      {KADIR_MARK_FOLDS.map(({ points }, i) => (
        <polygon key={points} points={points} fill={`url(#${id}-${i})`} />
      ))}
    </svg>
  );
}
